function invalid(field, message) {
  const error = new Error(message);
  error.code = 'INVALID_CALENDAR_RANGE';
  error.field = field;
  error.statusCode = 422;
  throw error;
}

function validateLocalDate(value, field) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    invalid(field, 'Expected a local date in YYYY-MM-DD format');
  }
  const [year, month, day] = value.split('-').map(Number);
  // setUTCFullYear avoids Date.UTC's special treatment of years 0 through 99.
  const date = new Date(Date.UTC(2000, month - 1, day));
  date.setUTCFullYear(year);
  if (year < 1 || date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    invalid(field, 'Local date does not exist');
  }
  return value;
}

function validateRange(range, field, yearFrom, yearTill) {
  if (!range || typeof range !== 'object' || Array.isArray(range)) invalid(field, 'Expected a date range');
  const fromDate = validateLocalDate(range.fromDate, `${field}.fromDate`);
  const tillDate = validateLocalDate(range.tillDate, `${field}.tillDate`);
  if (fromDate < yearFrom || fromDate > yearTill) invalid(`${field}.fromDate`, 'Date is outside the school year');
  if (tillDate < yearFrom || tillDate > yearTill) invalid(`${field}.tillDate`, 'Date is outside the school year');
  if (tillDate < fromDate) invalid(`${field}.tillDate`, 'Range end precedes its start');
  return { fromDate, tillDate };
}

function compareText(left, right) {
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

function validateCalendar(config) {
  if (!config || typeof config !== 'object' || Array.isArray(config)) invalid('config', 'Expected a school calendar');
  const { schoolYearStart, timeZone } = config;
  if (!Number.isInteger(schoolYearStart) || schoolYearStart < 1 || schoolYearStart > 9998) {
    invalid('schoolYearStart', 'Expected a school year representable with four-digit local dates');
  }
  if (typeof timeZone !== 'string' || !timeZone.trim() || /^[+-]/.test(timeZone)) invalid('timeZone', 'Expected an IANA timezone');
  try {
    Intl.DateTimeFormat('en', { timeZone }).resolvedOptions();
  } catch (error) {
    invalid('timeZone', 'Expected an IANA timezone');
  }
  const yearFrom = `${String(schoolYearStart).padStart(4, '0')}-09-01`;
  const yearTill = `${String(schoolYearStart + 1).padStart(4, '0')}-08-31`;
  const teachingPeriod = validateRange(config.teachingPeriod, 'teachingPeriod', yearFrom, yearTill);
  if (!Array.isArray(config.breaks)) invalid('breaks', 'Expected a list of breaks');
  const ids = new Set();
  const breaks = config.breaks.map((item, index) => {
    const field = `breaks[${index}]`;
    if (!item || typeof item !== 'object' || Array.isArray(item)) invalid(field, 'Expected a named break');
    if (typeof item.breakId !== 'string' || !item.breakId.trim() || ids.has(item.breakId)) {
      invalid(`${field}.breakId`, 'Expected a unique nonempty break ID');
    }
    ids.add(item.breakId);
    if (typeof item.name !== 'string' || !item.name.trim()) invalid(`${field}.name`, 'Expected a nonempty break name');
    return { breakId: item.breakId, name: item.name.trim(), ...validateRange(item, field, yearFrom, yearTill) };
  });
  breaks.sort((left, right) => compareText(left.fromDate, right.fromDate) ||
    compareText(left.tillDate, right.tillDate) || compareText(left.breakId, right.breakId));
  return { schoolYearStart, timeZone, teachingPeriod, breaks };
}

function evaluateDate(localDate, config) {
  validateLocalDate(localDate, 'localDate');
  const calendar = validateCalendar(config);
  const exclusionReasons = [];
  if (localDate < calendar.teachingPeriod.fromDate || localDate > calendar.teachingPeriod.tillDate) {
    exclusionReasons.push({ kind: 'outsideTeachingPeriod' });
  }
  calendar.breaks.forEach(item => {
    if (localDate >= item.fromDate && localDate <= item.tillDate) exclusionReasons.push({ kind: 'break', ...item });
  });
  return { allowed: exclusionReasons.length === 0, exclusionReasons };
}

module.exports = { validateCalendar, evaluateDate };
